/**
 * FontConfigPanel
 *
 * Main UI for configuring per-platform font mappings.
 * Shows a grid: rows = font slots (8), columns = platforms (5).
 * Each cell is a dropdown with available system fonts.
 * First column shows editable display labels.
 *
 * @module font-config/components
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  ListSubheader,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  UploadFile as UploadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useFontConfig } from '../hooks/useFontConfig';
import {
  DEFAULT_PLATFORM_MAPPINGS,
  getFontsForPlatform,
} from '../data/systemFontCatalog';
import {
  getWebFont,
  getWebFontOptions,
  getCustomFontOptions,
  isWebFontId,
  webFontFallbackFamily,
} from '../data/googleFontCatalog';
import { collectActiveWebFonts } from '../utils/collectWebFonts';
import { getStudioWebFontsEnv } from '../utils/studioWebFontsEnv';
import { useEnsureWebFonts } from '../hooks/useEnsureWebFonts';
import { getFontConfigService } from '../services/FontConfigService';
import type { PlatformId, FontCategory } from '../types/fontConfig.types';
// FontUploadService is imported lazily inside the handlers below: it reads import.meta (through
// fontApiEnvironment), and a static import would pull that into every @modules/font-config consumer.

/** All font slots available for configuration */
const SCHEMA_FONTS = [
  // Sans-serif
  'SF Pro',
  'SF ProRounded',
  'Helvetica Neue',
  'Avenir Next',
  // Monospace
  'SF Mono',
  // Serif
  'New York',
  'Georgia',
  'Baskerville',
] as const;

/** Font category per font slot (for filtering dropdown options) */
const SCHEMA_FONT_CATEGORIES: Record<string, FontCategory> = {
  'SF Pro': 'sans-serif',
  'SF ProRounded': 'sans-serif',
  'Helvetica Neue': 'sans-serif',
  'Avenir Next': 'sans-serif',
  'SF Mono': 'monospace',
  'New York': 'serif',
  'Georgia': 'serif',
  'Baskerville': 'serif',
};

/** Platform columns in display order (iOS first — primary interest) */
const PLATFORMS: { id: PlatformId; label: string }[] = [
  { id: 'ios', label: 'iOS' },
  { id: 'android', label: 'Android' },
  { id: 'windows', label: 'Windows' },
  { id: 'macos', label: 'macOS' },
  { id: 'linux', label: 'Linux' },
];

interface FontConfigPanelProps {
  projectId: string;
}

export const FontConfigPanel: React.FC<FontConfigPanelProps> = ({ projectId }) => {
  const {
    config,
    loading,
    error,
    saving,
    getMapping,
    setMapping,
    setMappingForAllPlatforms,
    getFontLabel,
    setFontLabel,
    resetToDefaults,
  } = useFontConfig(projectId);

  // Preview platform toggle
  const [previewPlatform, setPreviewPlatform] = useState<PlatformId>('ios');

  // Load the project's active web fonts into the admin document so the
  // Preview section below renders them with the real glyphs.
  const activeWebFonts = useMemo(
    () => collectActiveWebFonts(config, { env: getStudioWebFontsEnv() }),
    [config],
  );
  useEnsureWebFonts(activeWebFonts);

  // Inline label editing state
  const [editingFont, setEditingFont] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleMappingChange = useCallback(
    (schemaFont: string, platform: PlatformId, fontName: string) => {
      setMapping(schemaFont, platform, fontName).catch(() => {});
    },
    [setMapping],
  );

  const handleApplyToAll = useCallback(
    (schemaFont: string, fontName: string) => {
      if (!fontName) return;
      setMappingForAllPlatforms(schemaFont, fontName).catch(() => {});
    },
    [setMappingForAllPlatforms],
  );

  /** Human name for a mapping value: web/upload ids show their family name. */
  const displayFontName = useCallback((value: string): string => {
    return getWebFont(value, config)?.family ?? value;
  }, [config]);

  // ── Custom font upload state ─────────────────────────────────────
  const [licenseAttested, setLicenseAttested] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleFontFileChosen = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = ''; // allow re-selecting the same file
      if (!file) return;
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(null);
      try {
        const { uploadCustomFont } = await import('../services/FontUploadService');
        const result = await uploadCustomFont(projectId, file);
        setUploadSuccess(`Uploaded ${result.font.cssFamily} (${result.font.weights.join(', ')})`);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Font upload failed');
      } finally {
        setUploading(false);
      }
    },
    [projectId],
  );

  /** Slots × platforms currently mapped to a given upload id. */
  const mappingsUsingFont = useCallback(
    (uploadId: string): string[] => {
      const hits: string[] = [];
      for (const [slot, perPlatform] of Object.entries(config.platformMappings ?? {})) {
        for (const [platform, value] of Object.entries(perPlatform ?? {})) {
          if (value === uploadId) hits.push(`${getFontLabel(slot)} / ${platform}`);
        }
      }
      return hits;
    },
    [config.platformMappings, getFontLabel],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;
    const uploadId = `upload:${deleteTargetId}`;
    setDeleting(true);
    setUploadError(null);
    try {
      // 1. Strip mappings FIRST (font still exists → no broken chains, and a
      //    later full-doc save can't resurrect the deleted entry).
      const affected = mappingsUsingFont(uploadId);
      if (affected.length > 0) {
        const strippedMappings: typeof config.platformMappings = {};
        for (const [slot, perPlatform] of Object.entries(config.platformMappings ?? {})) {
          const kept = Object.fromEntries(
            Object.entries(perPlatform ?? {}).filter(([, value]) => value !== uploadId),
          );
          if (Object.keys(kept).length > 0) strippedMappings[slot] = kept;
        }
        await getFontConfigService().saveConfig(projectId, {
          ...config,
          platformMappings: strippedMappings,
        });
      }
      // 2. Server takedown: GCS files + customFonts entry (dot-notation).
      const { deleteCustomFont } = await import('../services/FontUploadService');
      await deleteCustomFont(projectId, deleteTargetId);
      setUploadSuccess('Font deleted');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Font delete failed');
    } finally {
      setDeleting(false);
      setDeleteTargetId(null);
    }
  }, [deleteTargetId, mappingsUsingFont, config, projectId]);

  const handleReset = useCallback(() => {
    resetToDefaults().catch(() => {});
  }, [resetToDefaults]);

  const handleStartEdit = useCallback(
    (schemaFont: string) => {
      setEditingFont(schemaFont);
      setEditValue(getFontLabel(schemaFont));
    },
    [getFontLabel],
  );

  const handleSaveLabel = useCallback(() => {
    if (editingFont) {
      setFontLabel(editingFont, editValue.trim()).catch(() => {});
      setEditingFont(null);
    }
  }, [editingFont, editValue, setFontLabel]);

  const handleCancelEdit = useCallback(() => {
    setEditingFont(null);
  }, []);

  const handleLabelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSaveLabel();
      if (e.key === 'Escape') handleCancelEdit();
    },
    [handleSaveLabel, handleCancelEdit],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load font config: {error.message}</Alert>;
  }

  const hasOverrides =
    Object.keys(config.platformMappings).length > 0 ||
    (config.fontLabels && Object.keys(config.fontLabels).length > 0);

  return (
    <Box>
      {/* Mapping Grid */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Font Slot</TableCell>
              {PLATFORMS.map((p) => (
                <TableCell key={p.id} align="center" sx={{ fontWeight: 600, minWidth: 140 }}>
                  {p.label}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 600, minWidth: 150 }}>
                All platforms
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SCHEMA_FONTS.map((schemaFont) => {
              const label = getFontLabel(schemaFont);
              const isEditing = editingFont === schemaFont;
              const hasCustomLabel = label !== schemaFont;

              return (
                <TableRow key={schemaFont}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleLabelKeyDown}
                        size="small"
                        autoFocus
                        sx={{ minWidth: 130 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleSaveLabel} aria-label="Save font label">
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={handleCancelEdit} aria-label="Cancel label editing">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {label}
                          </Typography>
                          {hasCustomLabel && (
                            <Typography variant="caption" color="text.secondary">
                              {schemaFont}
                            </Typography>
                          )}
                          {!hasCustomLabel && (
                            <Typography variant="caption" color="text.secondary">
                              {SCHEMA_FONT_CATEGORIES[schemaFont]}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(schemaFont)}
                          sx={{ ml: 'auto', opacity: 0.5, '&:hover': { opacity: 1 } }}
                          aria-label={`Edit label for ${schemaFont}`}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                  {PLATFORMS.map((platform) => {
                    const currentFont = getMapping(schemaFont, platform.id);
                    const defaultFont = DEFAULT_PLATFORM_MAPPINGS[schemaFont]?.[platform.id];
                    const isOverridden = config.platformMappings?.[schemaFont]?.[platform.id] !== undefined;
                    const category = SCHEMA_FONT_CATEGORIES[schemaFont];
                    const availableFonts = getFontsForPlatform(platform.id, category);
                    const webFontOptions = getWebFontOptions(category);
                    const customFontOptions = getCustomFontOptions(config, category);

                    return (
                      <TableCell key={platform.id} align="center">
                        <Select
                          value={currentFont}
                          onChange={(e) =>
                            handleMappingChange(schemaFont, platform.id, e.target.value as string)
                          }
                          size="small"
                          disabled={saving}
                          data-testid={`font-select-${schemaFont.replace(/\s+/g, '-')}-${platform.id}`}
                          renderValue={(value) => displayFontName(value as string)}
                          sx={{
                            minWidth: 120,
                            fontSize: '0.813rem',
                            ...(isOverridden && {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: 2,
                              },
                            }),
                          }}
                        >
                          {availableFonts.map((font) => (
                            <MenuItem key={font.name} value={font.name}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{font.name}</span>
                                {font.name === defaultFont && (
                                  <Chip label="default" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                          {customFontOptions.length > 0 && (
                            <ListSubheader sx={{ lineHeight: '28px' }}>Custom fonts (uploaded)</ListSubheader>
                          )}
                          {customFontOptions.map((font) => (
                            <MenuItem key={font.id} value={font.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{font.family}</span>
                                <Chip label="custom" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                              </Box>
                            </MenuItem>
                          ))}
                          {webFontOptions.length > 0 && (
                            <ListSubheader sx={{ lineHeight: '28px' }}>Web fonts (same everywhere)</ListSubheader>
                          )}
                          {webFontOptions.map((font) => (
                            <MenuItem key={font.id} value={font.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{font.family}</span>
                                <Chip label="web" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    );
                  })}
                  {(() => {
                    const category = SCHEMA_FONT_CATEGORIES[schemaFont];
                    const webFontOptions = getWebFontOptions(category);
                    const customFontOptions = getCustomFontOptions(config, category);
                    const rowValues = PLATFORMS.map((p) => getMapping(schemaFont, p.id));
                    const uniformWebId =
                      rowValues.every((v) => v === rowValues[0]) && isWebFontId(rowValues[0], config)
                        ? rowValues[0]
                        : '';

                    return (
                      <TableCell align="center">
                        <Select
                          value={uniformWebId}
                          onChange={(e) => handleApplyToAll(schemaFont, e.target.value as string)}
                          size="small"
                          disabled={saving}
                          displayEmpty
                          data-testid={`font-select-all-${schemaFont.replace(/\s+/g, '-')}`}
                          inputProps={{ 'aria-label': `${label} — apply one font to all platforms` }}
                          renderValue={(value) =>
                            value ? displayFontName(value as string) : (
                              <Typography component="em" variant="caption" color="text.secondary">
                                Per platform
                              </Typography>
                            )
                          }
                          sx={{
                            minWidth: 130,
                            fontSize: '0.813rem',
                            ...(uniformWebId && {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: 2,
                              },
                            }),
                          }}
                        >
                          {customFontOptions.map((font) => (
                            <MenuItem key={font.id} value={font.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{font.family}</span>
                                <Chip label="custom" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                              </Box>
                            </MenuItem>
                          ))}
                          {webFontOptions.map((font) => (
                            <MenuItem key={font.id} value={font.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{font.family}</span>
                                <Chip label="web" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    );
                  })()}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ResetIcon />}
          onClick={handleReset}
          disabled={saving || !hasOverrides}
        >
          Reset to Defaults
        </Button>
        {saving && <CircularProgress size={20} />}
      </Box>

      {/* Custom fonts (project uploads) */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Custom fonts</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Upload TTF, OTF, WOFF or WOFF2 (max 5MB per file). Files are sanitized and
          self-hosted; upload Regular and Bold separately — they group into one family.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={licenseAttested}
              onChange={(e) => setLicenseAttested(e.target.checked)}
              inputProps={{ 'data-testid': 'font-upload-attestation' } as never}
            />
          }
          label={
            <Typography variant="caption">
              I confirm this project holds a license permitting web embedding of the fonts I upload
            </Typography>
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            disabled={!licenseAttested || uploading}
            data-testid="font-upload-button"
          >
            Upload font file
            <input
              hidden
              type="file"
              accept=".ttf,.otf,.woff,.woff2"
              onChange={handleFontFileChosen}
              data-testid="font-upload-input"
            />
          </Button>
          {uploading && <CircularProgress size={16} />}
        </Box>
        {uploadError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError(null)}>{uploadError}</Alert>}
        {uploadSuccess && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setUploadSuccess(null)}>{uploadSuccess}</Alert>}

        {Object.keys(config.customFonts ?? {}).length > 0 && (
          <Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {Object.entries(config.customFonts ?? {}).map(([fontId, font]) => (
              <Box
                key={fontId}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                  '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
                }}
              >
                <Typography sx={{ fontFamily: `'${font.cssFamily}', ${font.category}`, minWidth: 160 }}>
                  {font.cssFamily}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {font.faces.map((f) => `${f.weight}${f.style === 'italic' ? 'i' : ''}`).join(', ')} · {font.category}
                </Typography>
                {font.license?.fsTypeWarning && (
                  <Chip label="embedding bits warning" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.625rem' }} />
                )}
                <IconButton
                  size="small"
                  onClick={() => setDeleteTargetId(fontId)}
                  disabled={deleting}
                  aria-label={`Delete font family ${font.cssFamily}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Delete confirmation */}
      <Dialog open={deleteTargetId !== null} onClose={() => !deleting && setDeleteTargetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete custom font?</DialogTitle>
        <DialogContent>
          {(() => {
            if (!deleteTargetId) return null;
            const font = config.customFonts?.[deleteTargetId];
            const affected = mappingsUsingFont(`upload:${deleteTargetId}`);
            return (
              <>
                <Typography variant="body2">
                  “{font?.cssFamily ?? deleteTargetId}” will be removed from storage and can no
                  longer be used in funnels.
                </Typography>
                {affected.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Used by {affected.length} mapping{affected.length > 1 ? 's' : ''} — these
                    revert to defaults: {affected.join('; ')}
                  </Alert>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting} data-testid="font-delete-confirm">
            {deleting ? 'Deleting…' : 'Delete font'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Font Preview */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Typography variant="subtitle2">Preview</Typography>
          <ToggleButtonGroup
            value={previewPlatform}
            exclusive
            onChange={(_, val) => val && setPreviewPlatform(val)}
            size="small"
          >
            {PLATFORMS.map((p) => (
              <ToggleButton key={p.id} value={p.id} sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            bgcolor: 'background.default',
          }}
        >
          {SCHEMA_FONTS.map((schemaFont) => {
            const resolvedFont = getMapping(schemaFont, previewPlatform);
            const webFont = getWebFont(resolvedFont, config);
            const previewFamily = webFont
              ? `'${webFont.family}', '${webFontFallbackFamily(webFont.family)}', ${webFont.category}`
              : `'${resolvedFont}', ${SCHEMA_FONT_CATEGORIES[schemaFont]}`;
            const label = getFontLabel(schemaFont);
            return (
              <Box key={schemaFont} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.25 }}
                >
                  {label}{label !== schemaFont ? ` (${schemaFont})` : ''} → {webFont ? `${webFont.family} (web)` : resolvedFont}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: previewFamily,
                    fontSize: '1.125rem',
                    lineHeight: 1.4,
                  }}
                >
                  The quick brown fox jumps over the lazy dog. 0123456789
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FontConfigPanel;
